import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import {
  Award,
  Target,
  Eye,
  ShieldCheck,
  Sparkles,
  HeartHandshake,
  Lightbulb,
  MapPin,
  ArrowRight,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

const NAVY = '#1F2240'
const RED = '#E10F18'
const PURPLE = '#39367D'
const YELLOW = '#FFD44D'

const values = [
  {
    icon: ShieldCheck,
    title: 'Confianza',
    description:
      'Construimos relaciones duraderas basadas en la transparencia y la honestidad.',
  },
  {
    icon: Sparkles,
    title: 'Excelencia',
    description:
      'Nos esforzamos por ofrecer el mejor servicio y productos de calidad.',
  },
  {
    icon: HeartHandshake,
    title: 'Compromiso',
    description:
      'Estamos dedicados al éxito y bienestar de nuestros clientes.',
  },
  {
    icon: Lightbulb,
    title: 'Innovación',
    description:
      'Buscamos constantemente mejorar nuestros servicios y procesos.',
  },
] as const

const stats = [
  { value: '30+', label: 'Años de experiencia' },
  { value: '5', label: 'Sucursales' },
  { value: '1000+', label: 'Clientes satisfechos' },
  { value: '24/7', label: 'Soporte' },
] as const

const branches = [
  'La Altagracia',
  'Boca Chica',
  'Bonao',
  'San Pedro de Macorís',
  'Santo Domingo',
] as const

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <Helmet>
        <title>Sobre Nosotros | Oriental Ramirez</title>
        <meta
          name="description"
          content="Conoce a Oriental Ramirez: más de 30 años líderes en la venta de motocicletas y vehículos en República Dominicana. Nuestra historia, misión, visión y valores."
        />
        <link rel="canonical" href="https://orientalramirez.com/nosotros" />
      </Helmet>

      <main className="bg-white">
        {/* Hero */}
        <section
          className="relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${NAVY} 0%, ${PURPLE} 60%, ${RED} 130%)`,
          }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-20 blur-3xl"
            style={{ background: YELLOW }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-32 -left-20 h-80 w-80 rounded-full opacity-20 blur-3xl"
            style={{ background: RED }}
          />
          <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
            <span
              className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-[#1F2240]"
              style={{ background: YELLOW }}
            >
              <Award className="h-4 w-4" aria-hidden="true" />
              Más de 30 años de trayectoria
            </span>
            <h1 className="mt-6 max-w-3xl text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Conduciendo el progreso de República Dominicana
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-white/80 sm:text-xl">
              Somos Oriental Ramirez: líderes en la venta de motocicletas y
              vehículos, comprometidos con el desarrollo económico de nuestras
              comunidades.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                size="lg"
                className="h-12 bg-[#E10F18] text-white hover:bg-[#E10F18]/90"
              >
                <Link to="/catalogo">
                  Ver catálogo
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="h-12 border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
              >
                <Link to="/sucursales">Sucursales</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Quiénes somos */}
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-[#1F2240] sm:text-4xl">
                Quiénes somos
              </h2>
              <div className="mt-4 h-1 w-20 rounded-full bg-[#E10F18]" />
              <div className="mt-6 space-y-4 text-base leading-relaxed text-slate-600">
                <p>
                  En Oriental Ramírez nos dedicamos a ofrecer soluciones
                  financieras accesibles y personalizadas para nuestros
                  clientes en la República Dominicana. Fundados con el propósito
                  de apoyar a los emprendedores y trabajadores locales, nos
                  especializamos en motopréstamos, facilitando la adquisición de
                  motocicletas para quienes las necesitan en su actividad
                  económica y transporte diario.
                </p>
                <p>
                  Con sucursales en La Altagracia, Boca Chica, Bonao, San Pedro
                  de Macorís y Santo Domingo, estamos comprometidos a brindar un
                  servicio cercano y eficiente, adaptado a las necesidades de
                  cada comunidad. Nuestro enfoque está en proporcionar
                  financiamiento que impulse el desarrollo económico local y
                  mejore la calidad de vida de nuestros clientes.
                </p>
                <p>
                  Nuestro equipo está dedicado a ofrecer un servicio al cliente
                  excepcional y soluciones que fomenten el crecimiento y éxito
                  de quienes confían en nosotros.
                </p>
              </div>
              <p className="mt-6 text-xl font-bold text-[#39367D]">
                Tu éxito es nuestro compromiso.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl border border-slate-100 bg-slate-50 p-6 text-center"
                >
                  <div className="text-3xl font-extrabold text-[#E10F18] sm:text-4xl">
                    {stat.value}
                  </div>
                  <div className="mt-2 text-sm font-medium text-slate-600">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Misión y Visión */}
        <section className="bg-slate-50 py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border-slate-100 shadow-sm">
                <CardContent className="p-8">
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-xl text-white"
                    style={{ background: NAVY }}
                  >
                    <Target className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h2 className="mt-5 text-2xl font-bold text-[#1F2240]">
                    Nuestra misión
                  </h2>
                  <p className="mt-3 text-base leading-relaxed text-slate-600">
                    Facilitar el acceso a la movilidad personal y comercial a
                    través de soluciones financieras accesibles, contribuyendo
                    al desarrollo económico de las comunidades dominicanas.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-slate-100 shadow-sm">
                <CardContent className="p-8">
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-xl text-white"
                    style={{ background: PURPLE }}
                  >
                    <Eye className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h2 className="mt-5 text-2xl font-bold text-[#1F2240]">
                    Nuestra visión
                  </h2>
                  <p className="mt-3 text-base leading-relaxed text-slate-600">
                    Ser la empresa líder en financiamiento de motocicletas en
                    República Dominicana, reconocida por nuestra confiabilidad,
                    innovación y compromiso con el éxito de nuestros clientes.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Valores */}
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-[#1F2240] sm:text-4xl">
              Nuestros valores
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-slate-600">
              Los principios que guían cada decisión y cada relación con
              nuestros clientes.
            </p>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((value) => {
              const Icon = value.icon
              return (
                <Card
                  key={value.title}
                  className="group border-slate-100 shadow-sm transition-shadow hover:shadow-md"
                >
                  <CardContent className="p-6">
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-xl text-[#1F2240] transition-transform group-hover:scale-110"
                      style={{ background: YELLOW }}
                    >
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <h3 className="mt-5 text-lg font-bold text-[#1F2240]">
                      {value.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </section>

        {/* Sucursales */}
        <section className="bg-slate-50 py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight text-[#1F2240] sm:text-4xl">
                Presentes en todo el país
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-base text-slate-600">
                Cinco sucursales para estar cerca de ti.
              </p>
            </div>
            <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {branches.map((branch) => (
                <li
                  key={branch}
                  className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white p-5"
                >
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white"
                    style={{ background: RED }}
                  >
                    <MapPin className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="font-semibold text-[#1F2240]">{branch}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CTA */}
        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div
            className="relative overflow-hidden rounded-3xl px-6 py-14 text-center sm:px-12"
            style={{
              background: `linear-gradient(135deg, ${NAVY} 0%, ${PURPLE} 100%)`,
            }}
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-20 blur-3xl"
              style={{ background: YELLOW }}
            />
            <h2 className="relative text-3xl font-bold tracking-tight text-white sm:text-4xl">
              ¿Listo para encontrar tu próximo vehículo?
            </h2>
            <p className="relative mx-auto mt-4 max-w-2xl text-base text-white/80 sm:text-lg">
              Explora nuestro catálogo o visita la sucursal más cercana. Nuestro
              equipo está listo para ayudarte.
            </p>
            <div className="relative mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Button
                asChild
                size="lg"
                className="h-12 bg-[#E10F18] text-white hover:bg-[#E10F18]/90"
              >
                <Link to="/catalogo">
                  Ver catálogo
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="h-12 border-white/40 bg-white/10 text-white hover:bg-white/20 hover:text-white"
              >
                <Link to="/sucursales">Sucursales</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}
